import * as React from "react";
import { useParams, useSearchParams } from "react-router";
import { DriveBasePage } from "../driveBasePage";
import { useGetFileDetailsQuery } from "@/api/files";
import { useDriveList } from "../hooks/use-drive-list";
import { useDriveSearch } from "../hooks/use-drive-search";
import { mapSearchModeToApi, SearchMode } from "../search/types";
import TopBarProgress from "react-topbar-progress-indicator";

export const SubfolderDriveView: React.FC = () => {
  const { fseId: folderId } = useParams<{ fseId: string }>();
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q");
  const mode = searchParams.get("mode") as SearchMode | null;

  if (query) {
    return <SubfolderDrivePageSearch folderId={folderId || ""} query={query} mode={mode} />;
  } else {
    return <SubfolderDrivePageDefault folderId={folderId || ""} />;
  }
};

interface SubfolderDrivePageSearchProps {
  folderId: string;
  query: string;
  mode: SearchMode | null;
}

const SubfolderDrivePageSearch: React.FC<SubfolderDrivePageSearchProps> = ({ folderId, query, mode }) => {
  const { isLoading, error, displayItems, totalPages, setCurrentPage, itemsPerPage, currentPage, refetch } =
    useDriveSearch({
      query,
      folderId,
      search_mode: mapSearchModeToApi(mode),
    });
  const {
    data,
    isLoading: isLoadingDetails,
    error: detailsError,
    refetch: refetchDetails,
  } = useGetFileDetailsQuery({ fse_id: folderId });

  React.useEffect(
    () => () => {
      setCurrentPage(1);
    },
    [setCurrentPage, folderId],
  );

  return (
    <DriveBasePage
      totalPages={totalPages}
      setCurrentPage={setCurrentPage}
      itemsPerPage={itemsPerPage}
      currentPage={currentPage}
      isLoading={isLoading || isLoadingDetails}
      error={error || detailsError}
      displayItems={displayItems}
      isRootDirectory={false}
      data={data}
      folderId={folderId}
      isSearchMode={true}
      onRetry={() => Promise.all([refetch(), refetchDetails()])}
    />
  );
};

interface SubfolderDrivePageDefaultProps {
  folderId: string;
}

const SubfolderDrivePageDefault: React.FC<SubfolderDrivePageDefaultProps> = ({ folderId }) => {
  const {
    data,
    isLoading: isLoadingDetails,
    isFetching,
    error: detailsError,
    refetch: refetchDetails,
  } = useGetFileDetailsQuery({ fse_id: folderId });

  const { isLoading, error, displayItems, totalPages, setCurrentPage, itemsPerPage, currentPage, refetch } =
    useDriveList({
      folderId,
    });

  React.useEffect(
    () => () => {
      setCurrentPage(1);
    },
    [setCurrentPage, folderId],
  );

  return (
    <>
      {isFetching && <TopBarProgress />}
      <DriveBasePage
        totalPages={totalPages}
        setCurrentPage={setCurrentPage}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        isLoading={isLoading || isLoadingDetails}
        error={error || detailsError}
        displayItems={displayItems}
        isRootDirectory={false}
        data={data}
        folderId={folderId}
        onRetry={() => Promise.all([refetch(), refetchDetails()])}
      />
    </>
  );
};
